import { Button, confirm } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { useBreadcrumbPageLabel } from "@/app/layout/breadcrumbs/BreadcrumbLabelContext";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { getStockOrderDetailUrl } from "@/shared/lib/freedom-apps";
import {
  useConvertQuotationToInvoice,
  useConvertQuotationToSalesOrder,
  useQuotationInvoiceDetailQuery,
  useUpdateQuotationInvoiceDocumentStatus,
} from "../api/queries";
import {
  DocumentDetailTour,
  useDocumentDetailTour,
} from "../tour/DocumentDetailTour";
import { ConvertQuotationToInvoiceDialog } from "../ui/ConvertQuotationToInvoiceDialog";
import {
  ConvertQuotationToSalesOrderErrorDialog,
  type ConvertSalesOrderInvalidLine,
} from "../ui/ConvertQuotationToSalesOrderErrorDialog";
import { DocumentDetailContent } from "../ui/DocumentDetailContent";
import { IssueQuotationStockPrecheckDialog } from "../ui/IssueQuotationStockPrecheckDialog";
import { useQuotationIssueWithPrecheck } from "../lib/useQuotationIssueWithPrecheck";

export function QuotationInvoiceDocumentDetailPage() {
  const { id } = useParams({ from: "/app/documents/$id" });
  const nav = useNavigate({ from: "/app/documents/$id" });
  const { data, isLoading } = useQuotationInvoiceDetailQuery(id ?? "");
  useBreadcrumbPageLabel(data?.document?.documentNumber ?? undefined);
  const tour = useDocumentDetailTour();
  const convertToInvoice = useConvertQuotationToInvoice();
  const convertToSalesOrder = useConvertQuotationToSalesOrder();
  const updateStatus = useUpdateQuotationInvoiceDocumentStatus();
  const canUpdate = useActionPermission(["quotation_invoice:update"]);
  const issuePrecheck = useQuotationIssueWithPrecheck();
  const [convertInvoiceOpen, setConvertInvoiceOpen] = useState(false);
  const [convertSoErrorOpen, setConvertSoErrorOpen] = useState(false);
  const [convertSoInvalidLines, setConvertSoInvalidLines] = useState<
    ConvertSalesOrderInvalidLine[]
  >([]);

  const convertInvoiceBlockedReason = useMemo(() => {
    const h = data?.quotationInvoiceHeadroom;
    if (!h) return null;
    if (h.remaining <= 0.005) {
      return "ຍອດໃບບິນຄົບຕາມລາຄາແລ້ວ — ບໍ່ສາມາດສ້າງງວດໃໝ່";
    }
    return null;
  }, [data?.quotationInvoiceHeadroom]);

  const handleBack = () =>
    nav({
      to: "/app/documents",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
      },
    });

  const handleDownload = () => {
    if (id) {
      nav({
        to: "/app/documents/$id/pdf-preview",
        params: { id },
        search: { print: undefined },
      });
    }
  };

  const handlePrint = () => {
    if (id) {
      nav({
        to: "/app/documents/$id/pdf-preview",
        params: { id },
        search: { print: 1 },
      });
    }
  };

  return (
    <>
      <Header />
      <Main>
        <DocumentDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {isLoading && <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>}
        {data && (
          <DocumentDetailContent
            document={data.document}
            items={data.items}
            history={data.history}
            payments={data.payments}
            linkedInvoices={data.linkedInvoices ?? []}
            linkedSalesOrder={data.linkedSalesOrder ?? null}
            stockOrderDetailUrl={
              data.linkedSalesOrder
                ? getStockOrderDetailUrl(data.linkedSalesOrder.id)
                : null
            }
            stockReservations={data.stockReservations ?? []}
            onBack={handleBack}
            onDownload={handleDownload}
            onPrint={handlePrint}
            onStartTour={tour.startTour}
            onConvertToInvoice={() => setConvertInvoiceOpen(true)}
            convertToInvoiceDisabledReason={convertInvoiceBlockedReason}
            onConvertToSalesOrder={
              canUpdate &&
              data.document.documentType === "quotation" &&
              data.document.status === "issued" &&
              !data.linkedSalesOrder
                ? async () => {
                    if (!id) return;
                    const num =
                      data.document.documentNumber?.trim() ||
                      data.document.id.slice(0, 8);
                    const ok = await confirm({
                      title: "ແປງເປັນຄຳສັ່ງຂາຍ",
                      description: `ຢືນຢັນແປງລາຄາ #${num} ເປັນຄຳສັ່ງຂາຍ (draft)?`,
                      actionText: "ແປງ",
                    });
                    if (!ok) return;
                    convertToSalesOrder.mutate(id, {
                      onSuccess: (result) => {
                        window.open(
                          getStockOrderDetailUrl(result.salesOrderId),
                          "_blank",
                          "noopener,noreferrer",
                        );
                      },
                      onError: (err: unknown) => {
                        const e = err as Error & {
                          code?: string;
                          invalidLines?: ConvertSalesOrderInvalidLine[];
                        };
                        if (
                          e.code === "INVALID_LINES" &&
                          e.invalidLines?.length
                        ) {
                          setConvertSoInvalidLines(e.invalidLines);
                          setConvertSoErrorOpen(true);
                        }
                      },
                    });
                  }
                : undefined
            }
            convertToSalesOrderPending={convertToSalesOrder.isPending}
            onIssueDocument={
              canUpdate && data.document.status === "draft"
                ? () => {
                    if (!id) return;
                    const num =
                      data.document.documentNumber?.trim() ||
                      data.document.id.slice(0, 8);
                    issuePrecheck.requestIssue({
                      id,
                      documentNumber: num,
                      documentType: data.document.documentType,
                      onIssue: () =>
                        updateStatus.mutate({ id, status: "issued" }),
                    });
                  }
                : undefined
            }
            onCancelDocument={
              canUpdate && data.document.status !== "cancelled"
                ? async () => {
                    if (!id) return;
                    const num =
                      data.document.documentNumber?.trim() ||
                      data.document.id.slice(0, 8);
                    const ok = await confirm({
                      title: "ຍົກເລີກເອກະສານ",
                      description: `ຢືນຢັນຍົກເລີກເອກະສານ #${num}? ການດຳເນີນການນີ້ມັກຈະບໍ່ຍົກຄືນ.`,
                      actionText: "ຍົກເລີກ",
                      ActionProps: { variant: "destructive" },
                    });
                    if (ok) updateStatus.mutate({ id, status: "cancelled" });
                  }
                : undefined
            }
          />
        )}
        {data && data.document.documentType === "quotation" && id && (
          <ConvertQuotationToInvoiceDialog
            open={convertInvoiceOpen}
            onOpenChange={setConvertInvoiceOpen}
            currency={data.document.currency}
            headroom={data.quotationInvoiceHeadroom}
            initialItems={data.items}
            initialNotes={data.document.notes}
            initialDiscount={data.document.discountAmount}
            initialTaxRate={
              data.document.subtotal - data.document.discountAmount > 0
                ? data.document.taxAmount /
                  (data.document.subtotal - data.document.discountAmount)
                : 0
            }
            submitting={convertToInvoice.isPending}
            onConfirm={(body) => {
              convertToInvoice.mutate(
                { id, body },
                { onSuccess: () => setConvertInvoiceOpen(false) },
              );
            }}
          />
        )}
        <ConvertQuotationToSalesOrderErrorDialog
          open={convertSoErrorOpen}
          onOpenChange={setConvertSoErrorOpen}
          invalidLines={convertSoInvalidLines}
        />
        <IssueQuotationStockPrecheckDialog {...issuePrecheck.precheckDialogProps} />
        {!data && !isLoading && (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປລາຍການເອກະສານ
            </Button>
            <p className="text-muted-foreground">ບໍ່ພົບເອກະສານ</p>
          </div>
        )}
      </Main>
    </>
  );
}
